import { useEffect, useState, useCallback } from "react";
import { api, apiError, fmtINR } from "../lib/api";
import { useAuth, can } from "../context/AuthContext";
import { PageHeader } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, MagnifyingGlass, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", phone: "", city: "", gstin: "" };

export default function Customers() {
  const { user } = useAuth();
  const editable = can(user, ["admin", "ops", "finance"]);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [noteText, setNoteText] = useState("");

  const load = useCallback(() => {
    api.get("/customers", { params: { search } }).then((r) => setRows(r.data));
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm(c); setEditId(c.id); setOpen(true); };

  const save = async () => {
    try {
      if (editId) await api.put(`/customers/${editId}`, form);
      else await api.post("/customers", form);
      toast.success(`Customer ${editId ? "updated" : "created"}`);
      setOpen(false);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const viewCustomer = async (c) => {
    const { data } = await api.get(`/customers/${c.id}`);
    setDetail(data);
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/customers/${detail.id}/notes`, { text: noteText });
      setNoteText("");
      viewCustomer(detail);
      toast.success("Note added");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle="Profiles, activity & support notes"
        actions={editable && (
          <Button data-testid="add-customer-btn" onClick={openNew} className="rounded-sm gap-2">
            <Plus size={16} weight="bold" /> Add Customer
          </Button>
        )}
      />
      <div className="p-8 space-y-4">
        <div className="relative max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="customer-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, email…" className="rounded-sm pl-9" />
        </div>
        <div className="border border-border rounded-sm bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>City</TableHead>
                <TableHead>GSTIN</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody data-testid="customers-table">
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center py-10 text-muted-foreground">No customers found</TableCell></TableRow>
              )}
              {rows.map((c) => (
                <TableRow key={c.id} className="hover:bg-secondary/30 cursor-pointer" onClick={() => viewCustomer(c)}>
                  <TableCell className="font-semibold">{c.name}</TableCell>
                  <TableCell>{c.phone || "—"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{c.email || "—"}</TableCell>
                  <TableCell>{c.city || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{c.gstin || "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`customer-view-${c.id}`}
                      onClick={(e) => { e.stopPropagation(); viewCustomer(c); }}>
                      <Eye size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="customer-form">
          <SheetHeader><SheetTitle className="font-heading">{editId ? "Edit Customer" : "New Customer"}</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            {[
              ["name", "Name"], ["email", "Email"], ["phone", "Phone"], ["city", "City"], ["gstin", "GSTIN (optional)"],
            ].map(([k, l]) => (
              <div key={k}>
                <Label className="label-caps">{l}</Label>
                <Input data-testid={`customer-field-${k}`} value={form[k] || ""}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="rounded-sm mt-1.5" />
              </div>
            ))}
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={save} data-testid="customer-save">Save</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="customer-detail">
          <SheetHeader>
            <SheetTitle className="font-heading">{detail?.name}</SheetTitle>
          </SheetHeader>
          {detail && (
            <Tabs defaultValue="info" className="mt-4">
              <TabsList className="rounded-sm">
                <TabsTrigger value="info" className="rounded-sm">Info</TabsTrigger>
                <TabsTrigger value="activity" className="rounded-sm">Activity</TabsTrigger>
                <TabsTrigger value="notes" className="rounded-sm">Notes</TabsTrigger>
              </TabsList>
              <TabsContent value="info" className="space-y-3 pt-4 text-sm">
                {[["Email", detail.email], ["Phone", detail.phone], ["City", detail.city], ["GSTIN", detail.gstin]].map(([l, v]) => (
                  <div key={l} className="flex justify-between border-b border-border pb-2">
                    <span className="label-caps">{l}</span><span className="font-medium">{v || "—"}</span>
                  </div>
                ))}
                <Button variant="outline" className="rounded-sm w-full mt-4" onClick={() => { setDetail(null); openEdit(detail); }}>Edit Customer</Button>
              </TabsContent>
              <TabsContent value="activity" className="pt-4">
                {(!detail.transactions || detail.transactions.length === 0)
                  ? <p className="text-sm text-muted-foreground">No linked transactions.</p>
                  : detail.transactions.map((t) => (
                    <div key={t.id} className="border border-border rounded-sm p-3 mb-2 flex justify-between text-sm">
                      <div><StatusBadge value={t.txn_type} /> <span className="ml-2">{t.party_name}</span></div>
                      <span className="font-mono">{fmtINR(t.total_amount)}</span>
                    </div>
                  ))}
              </TabsContent>
              <TabsContent value="notes" className="pt-4 space-y-3" data-testid="customer-notes">
                <div className="flex gap-2">
                  <Input data-testid="note-input" value={noteText} onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add internal note…" className="rounded-sm" />
                  <Button className="rounded-sm" onClick={addNote} data-testid="note-add">Add</Button>
                </div>
                {(!detail.notes || detail.notes.length === 0) && <p className="text-sm text-muted-foreground">No notes yet.</p>}
                {detail.notes?.slice().reverse().map((n) => (
                  <div key={n.id} className="border border-border rounded-sm p-3 text-sm">
                    <p>{n.text}</p>
                    <p className="text-xs text-muted-foreground mt-1">{n.by} · {new Date(n.at).toLocaleString("en-IN")}</p>
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}
